import { AddModalPresenter } from "./AddModalPresenter";
import { useState } from "react";
import { ValidateMsg } from "api/interfaces/commonInterface.interface";
import useApiError from "hook/useApiError";
import { ReleaseNoteCreateResult } from "api/interfaces/settingsInterface.interface";
import { getTodayToUTCDate } from "utils/timeUtil";
import { ProfileAccountInfo } from "api/interfaces/accountInterface.interface";
import { useAppSelector } from "redux/hooks";
import { useAWS } from "hook/useAWS";
import { checkEmpty } from "utils/functions";

interface Props {
  selectedRow?: ReleaseNoteCreateResult | undefined;
  onModal: () => void;
  onDelete?: (id: string) => void;
  onUpdate: (data: ReleaseNoteCreateResult) => void;
  isLoading?: boolean;
  isOpen: boolean;
}

const initialForm: ReleaseNoteCreateResult = {
  version: "",
  content: "",
  isActive: false,
  createdAt: "",
  releasedAt: "",
  createdBy: "",
  createdEmail: "",
};

export const firmwareErrorKey = "label.setting.firmware.errorCode";

export function AddModalContainer(props: Props): JSX.Element {
  const { lang, intlString } = useApiError();
  const { docScan } = useAWS();
  const selectedAccount: ProfileAccountInfo = useAppSelector(
    (state) => state.accountSettings
  );
  const [validateMsg, setValidateMsg] = useState<ValidateMsg>({
    title: "",
    message: "",
  });

  const [form, setForm] = useState<ReleaseNoteCreateResult>(() => {
    if (props.selectedRow !== undefined) {
      return props.selectedRow;
    }
    return {
      ...initialForm,
      releasedAt: getTodayToUTCDate(selectedAccount.timezone),
    };
  });
  const onChange = (e: any) => {
    let {
      target: { value, name },
    } = e;

    setForm((prev) => {
      return {
        ...prev,
        [name]: value,
      };
    });
  };

  const onValidate = async (form: ReleaseNoteCreateResult) => {
    if (checkEmpty(form.version)) {
      setValidateMsg((info) => {
        return {
          ...info,
          version: intlString(lang.validateMsg.firmware.version),
        };
      });
      return true;
    }
    const allLists = await docScan(
      process.env.REACT_APP_RELEASE_NOTES_TABLE_NAME as string
    ).then((data: any) => {
      return data.Items;
    });
    if (
      allLists.some(
        (item: any) => item.version === form.version && item.id !== form.id
      )
    ) {
      setValidateMsg((info) => {
        return {
          ...info,
          version: intlString(lang.validateMsg.firmware.duplicateVersion),
        };
      });
      return true;
    }
    if (checkEmpty(form.content)) {
      setValidateMsg((info) => {
        return {
          ...info,
          content: intlString(lang.validateMsg.firmware.releaseNote),
        };
      });
      return true;
    }
    return false;
  };

  const onSave = async () => {
    setValidateMsg((info) => {
      return { title: "", message: "" };
    });

    if (await onValidate(form)) {
      return;
    }
    props.onUpdate(form);
  };

  return (
    <AddModalPresenter
      onModal={props.onModal}
      onChange={onChange}
      form={form}
      onDelete={props.onDelete}
      validateMsg={validateMsg}
      onSave={onSave}
      selectedRow={props.selectedRow}
      isLoading={props.isLoading ?? false}
    />
  );
}
